import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { useSpotifyStore } from './spotify'
import { logger } from '@/lib/logger'
import { getDataProvider } from '@/lib/dataProvider'

// TypeScript interfaces based on the database schema
export interface SpotifyNode {
  id: string
  user_id: string
  name: string
  spotify_url: string | null
  volume: number
  playback_target: string | null // 'here' for editor, display UUID, or null (defaults to first display)
  position_x: number
  position_y: number
  created_at: string
  updated_at: string
  container_id: string | null
}

export interface CreateSpotifyNodeData {
  name: string
  spotify_url: string
  position_x?: number
  position_y?: number
}

export interface UpdateSpotifyNodeData {
  name?: string
  spotify_url?: string
  volume?: number
  playback_target?: string | null
  position_x?: number
  position_y?: number
  container_id?: string | null
}

export interface SpotifyUrlInfo {
  type: 'track' | 'playlist' | 'album' | 'artist'
  id: string
  url: string
}

export const useSpotifyNodesStore = defineStore('spotifyNodes', () => {
  // State
  const spotifyNodes = ref<Map<string, SpotifyNode>>(new Map())
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  const isLoading = ref(false)

  // Getters
  const allSpotifyNodes = computed(() => {
    return Array.from(spotifyNodes.value.values())
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  const getSpotifyNodeById = computed(() => (id: string) => {
    return spotifyNodes.value.get(id)
  })

  const isSpotifyNodeLoading = computed(() => (id: string) => {
    return loadingStates.value.get(id) || false
  })

  // Utility functions
  const parseSpotifyUrl = (url: string): SpotifyUrlInfo | null => {
    try {
      // Match Spotify URLs like https://open.spotify.com/track/0Z3Vt4qyrNukxFRhQvhrWG
      const regex = /^https:\/\/open\.spotify\.com\/(track|playlist|album|artist)\/([a-zA-Z0-9]+)(\?.*)?$/
      const match = url.match(regex)
      
      if (!match) {
        return null
      }

      const [, type, id] = match
      return {
        type: type as 'track' | 'playlist' | 'album' | 'artist',
        id,
        url: url.split('?')[0] // Remove query parameters
      }
    } catch (error) {
      logger.error('Error parsing Spotify URL:', error)
      return null
    }
  }

  const fetchSpotifyMetadata = async (urlInfo: SpotifyUrlInfo): Promise<{ name: string; image_url?: string } | null> => {
    const spotifyStore = useSpotifyStore()
    
    try {
      // Only fetch for tracks and playlists
      if (!['track', 'playlist'].includes(urlInfo.type)) {
        return null
      }
      
      const data = await spotifyStore.getTrackOrPlaylist(urlInfo.type as 'track' | 'playlist', urlInfo.id)
      
      if (data) {
        const name = data.name
        let image_url = null
        
        if (urlInfo.type === 'track' && data.album?.images?.[0]) {
          image_url = data.album.images[0].url
        } else if (urlInfo.type === 'playlist' && data.images?.[0]) {
          image_url = data.images[0].url
        }
        
        return { 
          name: name || 'Unknown', 
          image_url: image_url || undefined 
        }
      }
      
      return null
    } catch (error) {
      logger.error('Error fetching Spotify metadata:', error)
      // Fall back to placeholder
      const capitalizedType = urlInfo.type.charAt(0).toUpperCase() + urlInfo.type.slice(1)
      return {
        name: `${capitalizedType} (${urlInfo.id.substring(0, 8)}...)`
      }
    }
  }

  // Actions
  const fetchSpotifyNodes = async (): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const nodes = await provider.query<SpotifyNode>('spotify_nodes', {
        orderBy: [{ column: 'name', ascending: true }]
      })

      // Update store
      const newSpotifyNodes = new Map<string, SpotifyNode>()
      nodes.forEach(node => {
        newSpotifyNodes.set(node.id, node)
      })
      spotifyNodes.value = newSpotifyNodes

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const createSpotifyNode = async (data: CreateSpotifyNodeData): Promise<{ success: boolean; error?: string; data?: SpotifyNode }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    // Parse and validate Spotify URL
    const urlInfo = parseSpotifyUrl(data.spotify_url)
    if (!urlInfo) {
      return { success: false, error: 'Invalid Spotify URL' }
    }

    // Try to fetch metadata to get a better name
    let finalName = data.name
    try {
      const metadata = await fetchSpotifyMetadata(urlInfo)
      if (metadata) {
        finalName = metadata.name
      }
    } catch {
      // Continue with provided name if metadata fetch fails
      logger.warn('Could not fetch Spotify metadata, using provided name')
    }

    const createData = {
      user_id: authStore.user.id,
      name: finalName,
      spotify_url: urlInfo.url, // Use cleaned URL
      position_x: data.position_x || 0,
      position_y: data.position_y || 0
    }

    try {
      const provider = getDataProvider()
      const result = await provider.insert<SpotifyNode>('spotify_nodes', createData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      // Update store
      spotifyNodes.value.set(result.data.id, result.data)

      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  const updateSpotifyNode = async (id: string, data: UpdateSpotifyNodeData): Promise<{ success: boolean; error?: string; data?: SpotifyNode }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const updateData: Partial<SpotifyNode> = { ...data }

      // If updating spotify_url, validate it
      if (data.spotify_url) {
        const urlInfo = parseSpotifyUrl(data.spotify_url)
        if (!urlInfo) {
          return { success: false, error: 'Invalid Spotify URL' }
        }
        updateData.spotify_url = urlInfo.url
      }

      const provider = getDataProvider()
      const result = await provider.update<SpotifyNode>('spotify_nodes', id, updateData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      // Update store
      spotifyNodes.value.set(id, result.data)

      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  const deleteSpotifyNode = async (id: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const result = await provider.delete('spotify_nodes', id)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Update store
      spotifyNodes.value.delete(id)
      loadingStates.value.delete(id)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  // Duplicate an existing Spotify node
  const duplicateSpotifyNode = async (id: string): Promise<{ success: boolean; error?: string; data?: SpotifyNode }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    const original = spotifyNodes.value.get(id)
    if (!original) {
      return { success: false, error: 'Spotify node not found' }
    }

    isLoading.value = true

    try {
      const provider = getDataProvider()
      const createData = {
        user_id: authStore.user.id,
        name: `${original.name} (Copy)`,
        spotify_url: original.spotify_url,
        volume: original.volume,
        playback_target: original.playback_target,
        position_x: original.position_x + 50,
        position_y: original.position_y + 50,
        container_id: original.container_id
      }

      const result = await provider.insert<SpotifyNode>('spotify_nodes', createData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      spotifyNodes.value.set(result.data.id, result.data)

      logger.info('🎵 Duplicated Spotify node:', original.name)
      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    spotifyNodes: computed(() => spotifyNodes.value),
    error: computed(() => error.value),
    isLoading: computed(() => isLoading.value),

    // Getters
    allSpotifyNodes,
    getSpotifyNodeById,
    isSpotifyNodeLoading,

    // Utilities
    parseSpotifyUrl,
    fetchSpotifyMetadata,

    // Actions
    fetchSpotifyNodes,
    createSpotifyNode,
    updateSpotifyNode,
    deleteSpotifyNode,
    duplicateSpotifyNode
  }
})